import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import type { masteryBonusSubmission } from "./masteryBonusSubmission";

export function MasteryBonusForm({ student, currency, send, onClose, onSaved }: {
  student: { id: string; name: string };
  currency: string;
  send: ReturnType<typeof masteryBonusSubmission>;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [amount, setAmount] = useState("");
  const bonus = Number(amount);
  const validAmount = Number.isInteger(bonus) && bonus >= 1 && bonus <= 100000;
  const amountId = useId();
  const [note, setNote] = useState("");
  const [status, setStatus] = useState<"ready" | "saving" | "saved" | "uncertain">("ready");
  const [message, setMessage] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const noteId = useId();
  useEffect(() => {
    const trigger = document.activeElement;
    input.current?.focus();
    return () => { if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus(); };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (status !== "ready" || !validAmount || note.trim().length < 2) return;
    setStatus("saving");
    try {
      const result = await send(bonus, note);
      if (!result) {
        setStatus("uncertain");
        setMessage("This form has already submitted a bonus. Check this learner’s ledger before adding another.");
        return;
      }
      setStatus("saved");
      setMessage(`Saved ${result.bonus} ${currency} for ${student.name}.`);
    } catch {
      setStatus("uncertain");
      setMessage("We could not confirm the bonus. Check this learner’s ledger before adding it again.");
      return;
    }
    // Refresh failure must never make a completed ledger write resubmittable.
    try { await onSaved(); }
    catch { setMessage(`Bonus saved for ${student.name}. Refresh the dashboard to see the updated balance.`); }
  }

  return <section className="admin-card mastery-bonus-form" aria-label={`Parent bonus for ${student.name}`}>
    <form className="modal-form" onSubmit={event => void submit(event)}>
      <h3>Add an optional parent bonus for {student.name}</h3>
      <p>Choose an amount and leave a note. This optional gift does not mark math topics as learned.</p>
      <label htmlFor={amountId}>Bonus amount ({currency})</label>
      <input id={amountId} name="bonusAmount" type="number" min={1} max={100000} step={1} value={amount} onChange={event => setAmount(event.target.value)} required disabled={status !== "ready"} />
      <label htmlFor={noteId}>Note for {student.name}</label>
      <input ref={input} id={noteId} name="bonusNote" value={note} onChange={event => setNote(event.target.value)} required minLength={2} maxLength={240} disabled={status !== "ready"} />
      {message && <p role={status === "uncertain" ? "alert" : "status"}>{message}</p>}
      <div className="modal-actions">
        <button type="button" className="button ghost" onClick={onClose} disabled={status === "saving"}>{status === "ready" ? "Cancel" : "Close"}</button>
        <button type="submit" className="button primary" disabled={status !== "ready" || !validAmount || note.trim().length < 2}>
          {status === "saving" ? "Saving…" : status === "saved" ? "Bonus saved" : "Add bonus"}
        </button>
      </div>
    </form>
  </section>;
}
